import { formatterLong, numberFormatter } from '../../styles/GlobalStyles';
import { defaultPreferences, scoreWeightings } from '../../defaults';
import { setData } from '../firestore';
import { cloudFunctionV2 } from '../api';

export const makeId = (length) => {
    var id = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for ( var i = 0; i < length; i++ ) {
       id += characters.charAt(Math.floor(Math.random() * charactersLength));
	}

	return id;
}

export const formatPrice = (value) => {
	const checkNaN = isNaN(value);
	const stringVal = `${value}`;
	if ( checkNaN === true ) {
		return value;
	}
	else if ( stringVal === "" ) {
		return "";
	}
	else if ( stringVal.includes(".") ) {
		const fullStopIndex = stringVal.indexOf('.');
		const firstString = stringVal.substring(0, fullStopIndex);
		const secondString = stringVal.substring(fullStopIndex, stringVal.length);
		const dollarNumber = formatterLong.format(Number(firstString)).replace("$", "").replace(".00", "");
		const removeExcessDPs = secondString.substring(0, 3);
		const finalNumber = `${dollarNumber}${removeExcessDPs}`;
		return finalNumber;
	}
	else {
		const dollarNumber = formatterLong.format(value).replace("$", "").replace(".00", "");
		return dollarNumber;
	}
};

export const getPercentile = (arr, percentile) => {
    if (!Array.isArray(arr) || arr.length === 0) {
        throw new Error("Input must be a non-empty array.");
    }

    // Sort the array in ascending order
    const sortedArr = [...arr].sort((a, b) => a - b);

    const index = (percentile / 100) * (sortedArr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    // Interpolate if necessary
    return lower === upper
        ? sortedArr[lower]
        : sortedArr[lower] * (1 - weight) + sortedArr[upper] * weight;
};

export const calculateTokenCost = ({prompt_tokens, cached_tokens, completion_tokens}) => {
	const PRICE = {input: 10, cached: 2.5, output: 40};     // O3 Pricing per 1M tokens
	const uncached = prompt_tokens - cached_tokens;

	const inputCost   = (uncached         * PRICE.input)  / 1e6;
	const cachedCost  = (cached_tokens    * PRICE.cached) / 1e6;
	const outputCost  = (completion_tokens* PRICE.output) / 1e6;

	return {
		inputCost,
		cachedCost,
		outputCost,
		total: inputCost + cachedCost + outputCost
	};
}

export const queryGPT = async (gptURL, messages) => {
	const params = {
		service: "openai",
		messages,
		model: "o3",
		responseFormat: {
			type: "json_object"
		}
	};
	const res = await cloudFunctionV2(gptURL, params);
	if (res.status !== 200) {
		throw new Error(res.error || "GPT query failed");
	}
	const raw = res.data;
	// const usage = {
	// 	prompt_tokens: res.usage.prompt_tokens,
	// 	cached_tokens: res.usage.prompt_tokens_details.cached_tokens,
	// 	completion_tokens: res.usage.completion_tokens
	// };
	// const tokenCost = await calculateTokenCost(usage);
	return typeof raw === "string"
		? JSON.parse(raw.replace(/```json|```/g, "").trim())
		: raw;
};

export const squareFeetToAcres = (sqft) => {
    if (typeof sqft !== "number" || sqft < 0) return "N/A";
    
    const acres = sqft / 43560;
    
    // If less than 0.25 acres, return square footage
    if (acres < 0.25) {
        return `${numberFormatter.format(sqft)} sqft lot`;
    } 
    // If between 0.25 and 1 acre, round to 1 decimal place and ensure a trailing zero
    else if (acres < 1) {
        return `${(Math.round(acres * 10) / 10).toFixed(1)} acres`;
    } 
    // If exactly 1 acre, return without decimals or "s"
    else if (acres === 1) {
        return `1 acre`;
    }

    // If greater than 1 acre, round to 1 decimal place
    return `${Math.round(acres * 10) / 10} acres`;
};

export const getWordsFromCharacters = (characters, timestampsStart) => {
	if (!Array.isArray(characters) || !Array.isArray(timestampsStart)) {
		throw new Error("Invalid input data: characters or timestampsStart is not an array.");
	}

	const words = [];
	let currentWord = "";
	let currentWordStartIndex = null;

	for (let i = 0; i < characters.length; i++) {
		const char = characters[i];
		// Check if this character is whitespace (space, tab, newline, etc.)
		const isWhitespace = /\s/.test(char);

		if (!isWhitespace) {
			// Start of a new word?
			if (currentWord.length === 0) {
				currentWordStartIndex = i;
			}
				currentWord += char;
			} 
		else {
			// If we just ended a word, push it to the results
			if (currentWord.length > 0) {
				words.push({
					word: currentWord,
					startSec: timestampsStart[currentWordStartIndex]
				});
				// Reset for the next word
				currentWord = "";
				currentWordStartIndex = null;
			}
		}
	}

	// If the last character wasn't whitespace, we may have one more word to push
	if (currentWord.length > 0 && currentWordStartIndex !== null) {
		words.push({
			word: currentWord,
			startSec: timestampsStart[currentWordStartIndex]
		});
	}

  	return words;
}

export const saveDefaultPreferences = async(userId) => {
	return new Promise(async resolve => {
		const colRef = "settings";
		const writeData = await setData(colRef, userId, defaultPreferences);
		resolve(writeData);
	});
};

export const sendErrorEmail = async(params) => {
	const url = process.env.REACT_APP_ERROR_EMAIL;
	await cloudFunctionV2(url, params);	
};

export const getWeightedScore = (scores) => {
	const locationScore = scores[0];
	const recencyScore = scores[1];
	const similarityScore = scores[2];

	const locationWeighting = scoreWeightings[0];
	const recencyWeighting = scoreWeightings[1];
	const similarityWeighting = scoreWeightings[2];

	const overallScore = ((locationScore * locationWeighting) + (recencyScore * recencyWeighting) + (similarityScore * similarityWeighting));
	return overallScore;
};

/**
 * Score labels mapping for consistent formatting across the codebase
 * Maps internal bonus/penalty keys from comp scoring to human-readable labels
 */
const scoreLabels = {
	// Tier-related
	'TIER1_RELAX': 'Tier 1 Relaxation',
	'TIER2_MATCH': 'Tier 2 Match',
	'TIER3_MATCH': 'Tier 3 Match',
	'TIER2_VIOL': 'Tier 2 Violation',
	'TIER3_VIOL': 'Tier 3 Violation',
	
	// Curb appeal — legacy absolute-bucket rules
	'CURB_SUBJ_HIGH_COMP_LOW': 'Curb Appeal: Subject High vs Comp Low',
	'CURB_SUBJ_MED_COMP_LOW': 'Curb Appeal: Subject Medium vs Comp Low',
	'CURB_SUBJ_HIGH_COMP_MED': 'Curb Appeal: Subject High vs Comp Medium',
	'CURB_AT_LEAST_SUBJECT': 'Curb Appeal: Comp At Least Subject Level',
	// Curb appeal — v5 pairwise block-vs-block rules
	'CURB_VS_BLOCK_NOTICEABLY_BETTER': 'Block: Comp Block Noticeably Better',
	'CURB_VS_BLOCK_SLIGHTLY_BETTER': 'Block: Comp Block Slightly Better',
	'CURB_VS_BLOCK_SIMILAR': 'Block: Similar to Subject',
	'CURB_VS_BLOCK_SLIGHTLY_WORSE': 'Block: Comp Block Slightly Worse',
	'CURB_VS_BLOCK_NOTICEABLY_WORSE': 'Block: Comp Block Noticeably Worse',
	
	// Noise
	'NOISE_SAME_BUCKET': 'Noise: Same Level',
	'NOISE_WORSE_2_PLUS': 'Noise: Two or More Levels Worse',
	
	// Sizing
	'SQFT_WITHIN_5PCT': 'Square Footage: Within 5%',
	'SQFT_WITHIN_10PCT': 'Square Footage: Within 10%',
	'LOT_WITHIN_10PCT': 'Lot Size: Within 10%',

	// Materials
	'MATERIAL_BEST': 'Best Construction Material',
	'MATERIAL_WORST': 'Worst Construction Material',
	
	// Problem codes
	'POOL_MISMATCH': 'Pool Mismatch',
	'COMMUNITY_TYPE_MISMATCH': 'Community Type Mismatch',
	'BASEMENT_FINISH_MISMATCH': 'Basement Finish Mismatch',
	'BASEMENT_COMP_EXCEEDS': 'Basement: Comp Exceeds Subject',
	'BASEMENT_SUBJECT_EXCEEDS': 'Basement: Subject Exceeds Comp',
	'BATHROOM_COMP_EXCEEDS': 'Bathrooms: Comp Exceeds Subject',
	'BATHROOM_SUBJECT_EXCEEDS': 'Bathrooms: Subject Exceeds Comp',
	'CONDITION_UNCLEAR': 'Condition Unclear',
	'DYNAMIC_DRIVER_MISMATCH': 'Dynamic Driver Mismatch',
	'FAR_AWAY': 'Far From Subject',
	'GARAGE_COMP_EXCEEDS': 'Garage: Comp Exceeds Subject',
	'GARAGE_SUBJECT_EXCEEDS': 'Garage: Subject Exceeds Comp',
	'HARD_BOUNDARY_HIGHWAY': 'Hard Boundary: Highway',
	'HARD_BOUNDARY_OTHER': 'Hard Boundary: Major Arterial',
	'HARD_BOUNDARY_ARTERIAL_SOFT': 'Hard Boundary: Arterial (soft)',
	'HARD_BOUNDARY_RAILROAD': 'Hard Boundary: Railroad',
	'HARD_BOUNDARY_WATER': 'Hard Boundary: Water',
	'PRODUCT_TYPE_MISMATCH': 'Product Type Mismatch',
	'SCALE_MISMATCH': 'Scale Mismatch',
	'STYLE_MISMATCH': 'Style Mismatch',
	'STYLE_COMP_BETTER': 'Style: Comp Nicer Than Subject',
	'STYLE_SUBJECT_BETTER': 'Style: Subject Nicer Than Comp',
	'WEAK_COMP': 'Weak Comp',
	'BASEMENT_MISMATCH': 'Basement Mismatch',
	'FLOOD_ZONE_MISMATCH': 'Flood Zone Mismatch',
	'FLOOD_ZONE_MISMATCH_MINOR': 'Flood Zone Mismatch (Minor)',
	'FLOOD_ZONE_MISMATCH_SEVERE': 'Flood Zone Mismatch (Severe)',
	'FLOOD_ZONE_COMP_BETTER': 'Flood Zone: Comp Lower Risk Than Subject',
	'FLOOD_ZONE_COMP_BETTER_MINOR': 'Flood Zone: Comp Slightly Lower Risk (Minor)',
	'CONSTRUCTION_MATERIAL_EXCLUDED': 'Excluded Construction Material',
	'GARAGE_MISMATCH': 'Garage Mismatch',
	'STORIES_MISMATCH': 'Stories Mismatch',
	'YEAR_BUILT_MISMATCH': 'Year Built Mismatch',
	'BEDBATH_PATTERN_MISMATCH': 'Bed/Bath Pattern Mismatch',
	'BEDBATH_RATIO_MISMATCH': 'Bed/Bath Ratio Mismatch',
	'PREMIUM_SUBDIVISION_MISMATCH': 'Premium Subdivision Mismatch',
	'DISCOUNT_SUBDIVISION_MISMATCH': 'Discount Subdivision Mismatch',
	'CONDITION_NOT_REMODELED': 'Condition: Not Remodeled',
	'FINISH_LEVEL_MISMATCH': 'Finish Level Mismatch',
	'SQFT_TOO_BIG': 'Square Footage: Too Big',
	'SQFT_TOO_SMALL': 'Square Footage: Too Small',
	'LOT_TOO_BIG': 'Lot Size: Too Big',
	'LOT_TOO_SMALL': 'Lot Size: Too Small',
	'MISSING_PROPERTY_INFO': 'Missing Property Info',
	'HIGHWAY_OBSTACLE': 'Highway Obstacle',
	'WATER_OBSTACLE': 'Water Obstacle (River/Creek)',
	'RAILROAD_OBSTACLE': 'Railroad Obstacle',
	
	// Small bonuses
	'GATED_ALIGN': 'Gated/Master-Planned Community Alignment',
	'NO_PROBLEM_CODES': 'No Problem Codes',
	'FLOOD_IN_ALLOWED': 'Flood Zone: In Allowed Category',
	'SAME_SUBDIVISION': 'Same Subdivision',
	'IN_MARKET_BONUS': 'In-Market Bonus',
	'TIME_CONFIDENCE': 'Stale Comp (Market Drift)'
};

/**
 * Get human-readable label for a score key
 * @param {string} key - The score key (e.g., 'TIER2_MATCH', 'CURB_SUBJ_HIGH_COMP_LOW')
 * @returns {string} Human-readable label
 */
export const getHumanReadableScoreLabel = (key) => {
	if (!key || typeof key !== 'string') return key;
	
	// Try exact match first
	if (scoreLabels[key]) {
		return scoreLabels[key];
	}
	
	// Fallback: format the key nicely
	// All-caps snake_case (e.g., SAME_SUBDIVISION) — just title-case the words
	if (/^[A-Z0-9_]+$/.test(key)) {
		return key
			.toLowerCase()
			.replace(/_/g, ' ')
			.replace(/\b\w/g, l => l.toUpperCase())
			.trim();
	}
	// camelCase or mixed — insert spaces before capitals
	return key
		.replace(/_/g, ' ')
		.replace(/([A-Z])/g, ' $1')
		.replace(/\b\w/g, l => l.toUpperCase())
		.trim();
};

// Export the labels object for direct access if needed
export { scoreLabels };

